import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DiagnosticHeader } from "@/components/diagnostic/DiagnosticHeader";
import { QuestionCard } from "@/components/diagnostic/QuestionCard";
import { ResumeBanner } from "@/components/diagnostic/ResumeBanner";
import { fetchQuizDefinition, trackEvent } from "@/lib/api";
import { saveQuizState, loadQuizState, clearQuizState, hasConsent } from "@/lib/quizStorage";

function isSkipped(questionId, answers, skipRules) {
  return (skipRules || []).some(
    (rule) =>
      rule.question === questionId &&
      Object.entries(rule.skip_if).some(([triggerQ, values]) => values.includes(answers[triggerQ]))
  );
}

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [definition, setDefinition] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [resumeAvailable, setResumeAvailable] = useState(null); // etat sauvegarde en attente de decision
  const [interstitial, setInterstitial] = useState(null);
  const [scaleValue, setScaleValue] = useState(5);
  const [selectedOption, setSelectedOption] = useState(null);
  const advancingRef = useRef(false);
  const progressFiredRef = useRef({});

  const consented = hasConsent();

  // Chargement initial : definition publique + eventuelle session a reprendre.
  // Les fonctions referencees sont des imports de module (references stables).
  useEffect(() => {
    if (!consented) return undefined;
    let mounted = true;
    fetchQuizDefinition()
      .then((def) => {
        if (!mounted) return;
        setDefinition(def);
        const saved = loadQuizState();
        if (saved && saved.answers && Object.keys(saved.answers).length > 0 && !saved.completed) {
          setResumeAvailable(saved);
        } else {
          trackEvent("diagnostic_started");
        }
      })
      .catch(() => mounted && setLoadError(true));
    return () => { mounted = false; };
  }, [consented]);

  const applicableQuestions = useMemo(() => {
    if (!definition) return [];
    return definition.questions.filter((q) => !isSkipped(q.id, answers, definition.skip_rules));
  }, [definition, answers]);

  const current = applicableQuestions[index];
  const total = applicableQuestions.length;
  const progress = total ? Math.round((index / total) * 100) : 0;

  const blockLabel = useMemo(() => {
    if (!definition || !current) return "";
    const block = definition.blocks.find((b) => b.key === current.block);
    return block ? block.label : "";
  }, [definition, current]);

  // Synchronise les etats locaux quand la question courante ou les reponses
  // changent. Sans effet visible sur la saisie en cours : la valeur relue est
  // identique a celle qui vient d'etre choisie.
  useEffect(() => {
    if (!current) return;
    const existing = answers[current.id];
    if (current.type === "scale") {
      setScaleValue(existing !== undefined && existing !== null && existing !== "" ? Number(existing) : 5);
    } else {
      setSelectedOption(existing ?? null);
    }
  }, [current, answers]);

  // Evenements de progression autorises : 25, 50, 75 pour cent. Aucun contenu.
  const fireProgressEvents = useCallback((nextIndex, totalCount) => {
    if (!totalCount) return;
    const ratio = nextIndex / totalCount;
    for (const threshold of [25, 50, 75]) {
      if (ratio >= threshold / 100 && !progressFiredRef.current[threshold]) {
        progressFiredRef.current[threshold] = true;
        trackEvent(`progress_${threshold}`);
      }
    }
  }, []);

  const goNext = useCallback(
    (newAnswers) => {
      const nextApplicable = definition.questions.filter((q) => !isSkipped(q.id, newAnswers, definition.skip_rules));
      const nextIndex = index + 1;

      if (nextIndex >= nextApplicable.length) {
        trackEvent("diagnostic_completed");
        saveQuizState({ answers: newAnswers, index: nextIndex, completed: true });
        navigate("/bilan");
        return;
      }

      fireProgressEvents(nextIndex, nextApplicable.length);

      const nextQuestion = nextApplicable[nextIndex];
      if (nextQuestion.block !== current.block) {
        const nextBlock = definition.blocks.find((b) => b.key === nextQuestion.block);
        if (!reduceMotion) {
          setInterstitial({ label: nextBlock ? nextBlock.label : "" });
          setTimeout(() => setInterstitial(null), 900);
        }
      }
      saveQuizState({ answers: newAnswers, index: nextIndex, completed: false });
      setIndex(nextIndex);
      advancingRef.current = false;
    },
    [definition, index, current, navigate, reduceMotion, fireProgressEvents]
  );

  const answerChoice = (value) => {
    if (advancingRef.current || !current) return;
    advancingRef.current = true;
    setSelectedOption(value);
    const newAnswers = { ...answers, [current.id]: value };
    setAnswers(newAnswers);
    setTimeout(() => goNext(newAnswers), 280);
  };

  const answerScale = () => {
    if (advancingRef.current || !current) return;
    advancingRef.current = true;
    const newAnswers = { ...answers, [current.id]: scaleValue };
    setAnswers(newAnswers);
    goNext(newAnswers);
  };

  const goBack = () => {
    advancingRef.current = false;
    if (index > 0) {
      const newIndex = index - 1;
      saveQuizState({ answers, index: newIndex, completed: false });
      setIndex(newIndex);
    } else {
      navigate("/");
    }
  };

  const resumeQuiz = () => {
    const saved = resumeAvailable;
    setAnswers(saved.answers || {});
    const applicable = definition.questions.filter((q) => !isSkipped(q.id, saved.answers || {}, definition.skip_rules));
    setIndex(Math.min(saved.index || 0, Math.max(applicable.length - 1, 0)));
    setResumeAvailable(null);
  };

  const restartQuiz = () => {
    clearQuizState();
    setAnswers({});
    setIndex(0);
    setResumeAvailable(null);
    progressFiredRef.current = {};
    trackEvent("diagnostic_started");
  };

  if (!consented) return <Navigate to="/" replace />;

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-[400px]">
          <h1 className="font-heading text-xl font-semibold text-[#060606]">Un instant…</h1>
          <p className="mt-2 text-sm text-[#333333]">
            Le diagnostic est momentanément indisponible. Réessaie dans quelques secondes.
          </p>
          <Button onClick={() => window.location.reload()} className="mt-6 rounded-xl bg-[#235277] hover:bg-[#1E4767]" data-testid="diagnostic-reload-button">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="min-h-screen max-w-[560px] mx-auto px-4 pt-10">
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-8 w-3/4 mt-10 rounded-lg" />
        <div className="space-y-3 mt-8">
          {["a", "b", "c", "d"].map((slot) => <Skeleton key={`skeleton-${slot}`} className="h-14 w-full rounded-2xl" />)}
        </div>
        <p className="mt-8 text-sm text-center text-[#9E9E9E]">Chargement du diagnostic…</p>
      </div>
    );
  }

  if (resumeAvailable) {
    return (
      <ResumeBanner
        answeredCount={Object.keys(resumeAvailable.answers || {}).length}
        onResume={resumeQuiz}
        onRestart={restartQuiz}
      />
    );
  }

  if (!current) return <Navigate to="/bilan" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <DiagnosticHeader progress={progress} index={index} total={total} onBack={goBack} />

      {/* Interstitiel de bloc */}
      <AnimatePresence>
        {interstitial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-[#235277] flex items-center justify-center px-6"
            data-testid="diagnostic-block-interstitial"
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.26 }}
              className="text-center"
            >
              <Check className="w-8 h-8 text-[#EAFE63] mx-auto" aria-hidden="true" />
              <p className="mt-3 text-sm font-medium text-[rgba(249,254,255,0.7)]">Bloc suivant</p>
              <p className="font-heading mt-1 text-xl font-semibold text-white">{interstitial.label}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question courante */}
      <main className="flex-1 max-w-[560px] w-full mx-auto px-4 pt-8 pb-28">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={reduceMotion ? {} : { opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduceMotion ? {} : { opacity: 0, x: -24 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <QuestionCard
              current={current}
              blockLabel={blockLabel}
              selectedOption={selectedOption}
              onChoice={answerChoice}
              scaleValue={scaleValue}
              onScaleChange={setScaleValue}
              onScaleContinue={answerScale}
            />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
